import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PublicLayout } from "@/components/site/PublicLayout";

import Home from "@/pages/Home";
import LoanPrograms from "@/pages/LoanPrograms";
import ProgramDetail from "@/pages/ProgramDetail";
import EMDFunding from "@/pages/EMDFunding";
import TransactionalFunding from "@/pages/TransactionalFunding";
import Partners from "@/pages/Partners";
import BecomePartner from "@/pages/BecomePartner";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Apply from "@/pages/Apply";
import LegalPage from "@/pages/LegalPage";

import { Login, Register, ForgotPassword, ResetPassword, AcceptInvite } from "@/pages/auth/AuthPages";
import AdminIntake from "@/pages/admin/AdminIntake";
import AdminTeam from "@/pages/admin/AdminTeam";
import AuthCallback from "@/pages/auth/AuthCallback";

import PortalLayout from "@/pages/portal/PortalLayout";
import { PortalDashboard, PortalApplications, PortalApplicationDetail, PortalDocuments, PortalMessages, PortalProfile, PortalReferrals } from "@/pages/portal/PortalPages";

import AdminLayout from "@/pages/admin/AdminLayout";
import { AdminDashboard, AdminSubmissions } from "@/pages/admin/AdminPages";
import AdminLenders from "@/pages/admin/AdminLenders";
import AdminPreapprovals from "@/pages/admin/AdminPreapprovals";
import AdminReferralPartners from "@/pages/admin/AdminReferralPartners";
import ReferralProgram from "@/pages/ReferralProgram";
import { AdminBorrowers, AdminReferrals, AdminContacts, AdminDocuments, AdminMessages, AdminPartners, AdminContent, AdminSettings } from "@/pages/admin/AdminMisc";

const Public = ({ children }) => <PublicLayout>{children}</PublicLayout>;

function AppRouter() {
  const location = useLocation();
  React.useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("ref");
    if (p) localStorage.setItem("bullseye_ref", p.toUpperCase());
  }, []);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Public><Home /></Public>} />
      <Route path="/loan-programs" element={<Public><LoanPrograms /></Public>} />
      <Route path="/loan-programs/:slug" element={<Public><ProgramDetail /></Public>} />
      <Route path="/emd-funding" element={<Public><EMDFunding /></Public>} />
      <Route path="/transactional-funding" element={<Public><TransactionalFunding /></Public>} />
      <Route path="/partners" element={<Public><Partners /></Public>} />
      <Route path="/become-a-partner" element={<Public><BecomePartner /></Public>} />
      <Route path="/about" element={<Public><About /></Public>} />
      <Route path="/contact" element={<Public><Contact /></Public>} />
      <Route path="/apply" element={<Public><Apply /></Public>} />
      <Route path="/referral-program" element={<Public><ReferralProgram /></Public>} />
      <Route path="/privacy" element={<Public><LegalPage kind="privacy" /></Public>} />
      <Route path="/terms" element={<Public><LegalPage kind="terms" /></Public>} />
      <Route path="/disclosures" element={<Public><LegalPage kind="disclosures" /></Public>} />

      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />

      <Route path="/portal" element={<ProtectedRoute><PortalLayout /></ProtectedRoute>}>
        <Route index element={<PortalDashboard />} />
        <Route path="applications" element={<PortalApplications />} />
        <Route path="applications/:id" element={<PortalApplicationDetail />} />
        <Route path="documents" element={<PortalDocuments />} />
        <Route path="messages" element={<PortalMessages />} />
        <Route path="profile" element={<PortalProfile />} />
        <Route path="referrals" element={<PortalReferrals />} />
      </Route>

      <Route path="/admin" element={<ProtectedRoute staff><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="loan" element={<AdminSubmissions type="loan" />} />
        <Route path="emd" element={<AdminSubmissions type="emd" />} />
        <Route path="transactional" element={<AdminSubmissions type="transactional" />} />
        <Route path="documents" element={<AdminDocuments />} />
        <Route path="borrowers" element={<AdminBorrowers />} />
        <Route path="partners" element={<AdminPartners />} />
        <Route path="lenders" element={<AdminLenders />} />
        <Route path="preapprovals" element={<AdminPreapprovals />} />
        <Route path="referrals" element={<AdminReferrals />} />
        <Route path="referral-partners" element={<AdminReferralPartners />} />
        <Route path="contacts" element={<AdminContacts />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="intake" element={<AdminIntake />} />
        <Route path="team" element={<AdminTeam />} />
      </Route>

      <Route path="*" element={<Public><Home /></Public>} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors theme="dark" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
